import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UserService } from '@app/features/users/services/user.service';
import { UserDTO } from '@app/features/users/models/user.model';
import {
  ButtonComponent,
  CheckboxComponent,
  FormFieldComponent,
  FormGroupComponent,
  FormRowComponent,
  IconComponent,
  InputDirective,
  LabelComponent,
  NotificationService,
  OptionComponent,
  PlaceholderComponent,
  SelectComponent,
  SubHeaderComponent,
  Table2Component
} from '@pansa/ui';
import { GroupService } from '../../services/group.service';
import { CreateGroupDTO, GroupDTO, GroupTypeEnum, UpdateGroupDTO } from '../../models/group.model';
import { Roles } from '@app/shared/models/roles.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { FieldErrorComponent } from '@app/shared/components/field-error/field-error.component';

@Component({
  selector: 'rqa-add-group',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    SubHeaderComponent,
    ButtonComponent,
    SelectComponent,
    OptionComponent,
    FormRowComponent,
    FormGroupComponent,
    FormFieldComponent,
    LabelComponent,
    InputDirective,
    PlaceholderComponent,
    IconComponent,
    Table2Component,
    CheckboxComponent,
    FieldErrorComponent
  ],
  templateUrl: './add-group.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-group.component.scss'
})
export class AddGroupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private groupService = inject(GroupService);
  private userService = inject(UserService);
  private location = inject(Location);
  private notificationService = inject(NotificationService)

  groupForm = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    group: ['', [Validators.required]],
    responsibleUnit: ['', [Validators.required]],
  });
  groupId: string | null = this.route.snapshot.paramMap.get('id');
  editMode = Boolean(this.groupId);
  filtersForm: FormGroup = this.fb.group({
    search: ['']
  });

  readonly users = signal<UserDTO[]>([]);
  readonly trackBy: keyof UserDTO = 'username';
  selectedUsers = signal<UserDTO[]>([]);

  someSelected = computed(() => {
    const filtered = this.filteredUsers();
    const selectedUsernames = new Set(this.selectedUsers().map((u) => u.username));

    const selectedCount = filtered.filter((u) => selectedUsernames.has(u.username)).length;

    return selectedCount > 0 && selectedCount < filtered.length;
  });

  allSelected = computed(() => {
    const filtered = this.filteredUsers();
    const selected = this.selectedUsers();

    return filtered.length > 0 && filtered.every((u) => selected.some((s) => s.username === u.username));
  });

  isSelected = (user: UserDTO) => {
    return this.selectedUsers().some((u) => u.username === user.username);
  };

  groupTypeOptions: string[] = Object.values(GroupTypeEnum);

  groupValue = toSignal(this.groupForm.get('group')!.valueChanges, {
    initialValue: this.groupForm.get('group')!.value
  });

  organizationValue = toSignal(this.groupForm.get('responsibleUnit')!.valueChanges, {
    initialValue: this.groupForm.get('responsibleUnit')!.value
  });

  searchValue = toSignal(this.filtersForm.get('search')!.valueChanges, {
    initialValue: this.filtersForm.get('search')!.value
  });

  filteredUsers = computed(() => {
    const group = this.groupValue();
    const organization = this.organizationValue() || '';
    const search = this.searchValue()?.toLowerCase() ?? '';
    const users = this.users();

    if (!group) return [];

    return users.filter(
      (user) =>
        user.group === group && (!search || Object.values(user).some((v) => String(v).toLowerCase().includes(search))) && user.responsibleUnit.toLowerCase().includes(organization?.toLowerCase())
    );
  });

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.userService.getUsersList().subscribe({
      next: (users) => {
        this.users.set(users.filter((user) => user.enabled === true && user.role !== Roles.WEBRQA_OBSERVER));
        if (this.editMode) {
          this.loadGroupData();
        }
      },

      error: (error) => {
        console.error('Error loading users:', error);
      }
    });
  }

  toggleUser(user: UserDTO) {
    this.selectedUsers.update((selected) =>
      selected.some((u) => u.username === user.username)
        ? selected.filter((u) => u.username !== user.username)
        : [...selected, user]
    );
  }

  toggleAll() {
    const filtered = this.filteredUsers();
    const selected = this.selectedUsers();

    if (this.allSelected()) {
      this.selectedUsers.set(selected.filter((u) => !filtered.some((f) => f.username === u.username)));
    } else {
      const newOnes = filtered.filter((f) => !selected.some((s) => s.username === f.username));

      this.selectedUsers.set([...selected, ...newOnes]);
    }
  }

  onSubmit(): void {
    this.groupForm.markAllAsTouched();
    if (this.groupForm.valid) {
      if (this.editMode && this.groupId) {
        const updateGroupData: UpdateGroupDTO = {
          id: this.groupId,
          name: this.groupForm.value.name!.trim(),
          group: this.groupForm.value.group!,
          users: this.selectedUsers().map((user) => user.username),
          responsibleUnit: this.groupForm.value.responsibleUnit as string
        };

        this.groupService.updateGroup(updateGroupData).subscribe({
          next: (response) => {
            this.notificationService.success({ title: 'Sukces', subTitle: `Zaktualizowano grupę ${updateGroupData.name}` });
            this.router.navigate(['/groups']);
            this.groupService.groupsResource.reload();
          },
          error: (error) => {
            console.error('Error updating group:', error);
          }
        });
      } else {
        const createGroupData: CreateGroupDTO = {
          name: this.groupForm.value.name!.trim(),
          group: this.groupForm.value.group!,
          users: this.selectedUsers().map((user) => user.username),
          responsibleUnit: this.groupForm.value.responsibleUnit as string,
        };

        this.groupService.addGroup(createGroupData).subscribe({
          next: (response) => {
            this.notificationService.success({ title: 'Sukces', subTitle: `Dodano grupę ${createGroupData.name}` });
            this.groupService.groupsResource.reload();
            this.router.navigate(['/groups']);
          },
          error: (error) => {
            console.error('Error creating group:', error);
          }
        });
      }
    }
  }

  onGoBack(): void {
    this.location.back();
  }

  private loadGroupData(): void {
    if (!this.groupId) return;

    this.groupService.getGroupById(this.groupId).subscribe({
      next: (group) => {
        this.populateFormWithGroupData(group);
      },
      error: (error) => {
        console.error('Error loading group:', error);
      }
    });
  }

  private populateFormWithGroupData(group: GroupDTO): void {
    this.groupForm.patchValue({
      name: group.name,
      group: group.groupValue,
      responsibleUnit: group.responsibleUnit
    });

    if (group.webRqaUsers && group.webRqaUsers.length > 0) {
      this.setSelectedUsersFromGroup(group.webRqaUsers);
    }
  }

  private setSelectedUsersFromGroup(groupUsers: UserDTO[]): void {
    const groupUsernames = groupUsers.map((user) => user.username);
    this.selectedUsers.set(this.users().filter((user) => groupUsernames.includes(user.username)));
  }
}
